import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useContact } from '../../../../hooks/useContact';
import { useNavigation } from '../../../../context/NavigationContext';
import { useResponsiveWidth } from '../../../../hooks/useResponsiveWidth';
import ContactDetails from './ContactDetails';
import SearchInput from '../../../SearchInput';
import ConversationList from '../../../ConversationList';
import useSearch from '../../../../hooks/useSearch';
import './ContactView.css';

const ContactView = ({
  contactId = null,
  isScreenWideEnoughForConversations = true,
}) => {
  const { t } = useI18n();
  const { navigateToConversation } = useNavigation();

  // Track ContactView width to auto-hide conversation list below 700px
  const { ref: contactViewRef, isWideEnough: isContactViewWideEnough } = useResponsiveWidth(700);
  
  // Combine both conditions - hide if EITHER ContactView is too narrow OR screen is too small
  // isScreenWideEnoughForConversations comes from parent (ContactsPage tracks 700px breakpoint)
  const isWideEnoughForConversations = isContactViewWideEnough && isScreenWideEnoughForConversations;

  // Use the new hook to get contact details and shared conversations
  const {
    contact,
    sharedConversations,
    isLoading,
  } = useContact(contactId);

  const {
    searchTerm,
    setSearchTerm,
  } = useSearch();

  // Navigation handler to go to conversation page
  const handleConversationClick = (conversationId) => {
    navigateToConversation(conversationId);
  };

  const [isConversationListOpen, setConversationListOpen] = useState(true);
  const [wasManuallyOpenedInNarrowMode, setWasManuallyOpenedInNarrowMode] = useState(false);
  const [wasManuallyClosedInWideMode, setWasManuallyClosedInWideMode] = useState(false);

  // Auto-hide conversation list when width becomes too narrow (via resize/zoom)
  // But only if it wasn't manually opened in narrow mode
  // Auto-show conversation list when width becomes sufficient again
  useEffect(() => {
    // Auto-hide when too narrow (unless manually opened in narrow mode)
    if (!isWideEnoughForConversations && isConversationListOpen && !wasManuallyOpenedInNarrowMode) {
      setConversationListOpen(false);
      // Clear manual close flag since we auto-closed
      setWasManuallyClosedInWideMode(false);
    }
    
    // Auto-show when width becomes sufficient again (unless user manually closed it)
    if (isWideEnoughForConversations && !isConversationListOpen && !wasManuallyOpenedInNarrowMode && !wasManuallyClosedInWideMode) {
      setConversationListOpen(true);
    }
    
    // Reset flags when crossing width threshold - they only apply within their respective modes
    if (!isWideEnoughForConversations && wasManuallyClosedInWideMode) {
      setWasManuallyClosedInWideMode(false);
    }
    if (isWideEnoughForConversations && wasManuallyOpenedInNarrowMode) {
      setWasManuallyOpenedInNarrowMode(false);
    }
  }, [isWideEnoughForConversations, isConversationListOpen, wasManuallyOpenedInNarrowMode, wasManuallyClosedInWideMode]);

  const handleConversationListToggle = useCallback(() => {
    const newOpenState = !isConversationListOpen;
    setConversationListOpen(newOpenState);
    
    // Track if user manually opened panel when in narrow mode
    if (newOpenState && !isWideEnoughForConversations) {
      setWasManuallyOpenedInNarrowMode(true);
      setWasManuallyClosedInWideMode(false); // Clear close flag when opening
    } else if (!newOpenState && isWideEnoughForConversations) {
      // User manually closed in wide mode - prevent auto-show
      setWasManuallyClosedInWideMode(true);
      setWasManuallyOpenedInNarrowMode(false); // Clear narrow mode flag
    } else if (!newOpenState) {
      // User closed it in narrow mode
      setWasManuallyOpenedInNarrowMode(false);
    } else if (newOpenState && isWideEnoughForConversations) {
      // User manually opened in wide mode - clear close flag
      setWasManuallyClosedInWideMode(false);
    }
  }, [isConversationListOpen, isWideEnoughForConversations]);

  const filteredConversations = sharedConversations.filter(conversation => conversation.title.toLowerCase().includes(searchTerm.toLowerCase()));

  // Calculate if conversation list should show and if it needs overlay mode
  const shouldShowConversationList = isConversationListOpen;
  const needsOverlayMode = shouldShowConversationList && !isWideEnoughForConversations;

  // Show loading state while fetching contact data
  if (!contactId) {
    return <div ref={contactViewRef} />;
  }

  if (isLoading && !contact) {
    return (
      <div className="contactview" ref={contactViewRef}>
        <div className="contactview-loading">
          {t('ContactView.loading', {}, 'Loading contact...')}
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="contactview" ref={contactViewRef}>
        <div className="contactview-error">
          {t('ContactView.notFound', {}, 'Contact not found')}
        </div>
      </div>
    );
  }

  return (
    <div className="contactview" ref={contactViewRef}>
      <div className={`contactview-contact-container ${!shouldShowConversationList || needsOverlayMode ? 'contactview-content-fill' : ''}`}>
        <ContactDetails
          contact={contact}
          onConversationListToggle={handleConversationListToggle}
        />
      </div>
      {
        isConversationListOpen && (
          <div className={`contactview-conversation-list-container ${needsOverlayMode ? 'overlay' : ''}`}>
            <div className="contactview-conversation-list-header">
              <SearchInput
                id="contact-conversation-search"
                value={searchTerm}
                placeholderValue={t('SearchInput.search')}
                onChange={setSearchTerm}
                onClearClick={() => setSearchTerm('')}
              />
              <span>{t('ContactConversationsView.conversations')}</span>
            </div>
            <div className="contactview-conversation-list-wrapper">
              <ConversationList
                conversations={filteredConversations}
                onItemClick={handleConversationClick}
                selectedConversationId={null}
                isLoading={isLoading}
              />
            </div>
          </div>
        )
      }
    </div>
  );
};

ContactView.propTypes = {
  contactId: PropTypes.string,
  isScreenWideEnoughForConversations: PropTypes.bool,
};

export default ContactView;
